import React from 'react'
import { ChevronDown } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface GeometricAccordionProps {
    title: string
    subtitle?: string
    badge?: React.ReactNode
    isOpen?: boolean
    onToggle?: () => void
    storageKey?: string
    defaultOpen?: boolean
    children: React.ReactNode
    className?: string
    headerRight?: React.ReactNode
}

// Fast GPU-friendly tween for content (no spring on height — springs cause jank on large DOM)
const CONTENT_TRANSITION = { duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }

export function GeometricAccordion({
    title,
    subtitle,
    badge,
    isOpen: controlledIsOpen,
    onToggle: controlledOnToggle,
    storageKey,
    defaultOpen = true,
    children,
    className = '',
    headerRight
}: GeometricAccordionProps) {
    const [internalIsOpen, setInternalIsOpen] = React.useState<boolean>(() => {
        if (storageKey && typeof window !== 'undefined') {
            const saved = localStorage.getItem(storageKey)
            if (saved !== null) return saved === 'true'
        }
        return defaultOpen
    })

    const isActuallyOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen

    const handleToggle = React.useCallback(() => {
        const nextValue = !isActuallyOpen
        if (storageKey && typeof window !== 'undefined') {
            localStorage.setItem(storageKey, String(nextValue))
        }
        if (controlledOnToggle) {
            controlledOnToggle()
        } else {
            setInternalIsOpen(nextValue)
        }
    }, [controlledOnToggle, isActuallyOpen, storageKey])

    return (
        <div className={`w-full rounded-[20px] border bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm transition-colors duration-300 ${isActuallyOpen ? 'border-indigo-200 dark:border-indigo-500/30 shadow-[0_4px_20px_-4px_rgba(99,102,241,0.08)]' : 'border-slate-200 dark:border-slate-800 shadow-sm hover:border-slate-300 dark:hover:border-slate-700'} ${className}`}>
            <div
                role="button"
                tabIndex={0}
                onClick={handleToggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
                className="w-full flex flex-wrap items-center justify-between gap-y-3 px-4 py-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-[20px] group cursor-pointer"
            >
                <div className="flex items-center gap-3">
                    {badge && (
                        <div className={`flex items-center justify-center w-8 h-8 rounded-lg font-bold text-[12px] transition-colors duration-300 ${isActuallyOpen ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 group-hover:bg-slate-200 dark:group-hover:bg-slate-700'}`}>
                            {badge}
                        </div>
                    )}
                    <div className="flex flex-col items-start">
                        <span className={`text-[14px] font-bold tracking-tight transition-colors duration-300 ${isActuallyOpen ? 'text-indigo-900 dark:text-indigo-100' : 'text-slate-900 dark:text-white group-hover:text-slate-700 dark:group-hover:text-slate-200'}`}>
                            {title}
                        </span>
                        {subtitle && (
                            <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-slate-400 dark:text-slate-500 mt-0.5">
                                {subtitle}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center ml-auto gap-2">
                    {headerRight && (
                        <div onClick={(e) => e.stopPropagation()} className="cursor-default flex items-center">
                            {headerRight}
                        </div>
                    )}
                    {/* Compact toggle button */}
                    <div 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggle();
                        }}
                        className={`flex items-center justify-center w-5 h-5 shrink-0 rounded-md cursor-pointer transition-all duration-200 ${isActuallyOpen ? 'bg-indigo-100/80 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400' : 'text-slate-400 group-hover:bg-slate-100 dark:group-hover:bg-slate-800 group-hover:text-slate-600 dark:group-hover:text-slate-300'}`}>
                        <ChevronDown 
                            size={13} 
                            strokeWidth={2.5}
                            className={`transition-transform duration-200 ease-out ${isActuallyOpen ? 'rotate-180' : 'rotate-0'}`}
                        />
                    </div>
                </div>
            </div>
            <div
                className={`grid transition-none lg:transition-[grid-template-rows] lg:duration-300 lg:ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
                    isActuallyOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                }`}
            >
                <div className="overflow-hidden">
                    <div className={`px-4 pb-4 pt-1 border-t border-slate-100 dark:border-slate-800/50 mt-0.5 transition-opacity duration-300 delay-100 ${isActuallyOpen ? 'opacity-100' : 'opacity-0'}`}>
                        {isActuallyOpen ? children : null}
                    </div>
                </div>
            </div>
        </div>
    )
}
